import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ResponsiveContainer, ComposedChart, Area, Line, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Download, GitCompareArrows, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const isoDate = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => isoDate(new Date(Date.now() - n * 86400000));

function fmtBytes(bytes) {
  const b = Number(bytes) || 0;
  if (b < 1024) return `${b} B`;
  const u = ["KB", "MB", "GB", "TB"];
  let v = b / 1024, i = 0;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
  return `${v >= 100 ? Math.round(v) : v.toFixed(1)} ${u[i]}`;
}

function fmtPeriod(p, bucket) {
  if (!p) return "";
  try {
    if (bucket === "hour") return new Date(`${p}:00`).toLocaleTimeString(undefined, { hour: "numeric" });
    if (bucket === "month") { const [y, m] = p.split("-"); return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "short", year: "2-digit" }); }
    return new Date(p).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return p; }
}

const PRESETS = [
  { label: "24h", build: () => ({ from: new Date(Date.now() - 86400000).toISOString(), to: new Date().toISOString(), bucket: "hour" }) },
  { label: "7d", build: () => ({ from: daysAgo(6), to: isoDate(new Date()), bucket: "day" }) },
  { label: "30d", build: () => ({ from: daysAgo(29), to: isoDate(new Date()), bucket: "day" }) },
  { label: "90d", build: () => ({ from: daysAgo(89), to: isoDate(new Date()), bucket: "day" }) },
  { label: "120d", build: () => ({ from: daysAgo(119), to: isoDate(new Date()), bucket: "week" }) },
  { label: "1y", build: () => ({ from: daysAgo(364), to: isoDate(new Date()), bucket: "week" }) },
  { label: "Last year", build: () => { const y = new Date().getFullYear() - 1; return { from: `${y}-01-01`, to: `${y}-12-31`, bucket: "month" }; } },
];

function TrendTooltip({ active, payload, label, bucket }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-lg">
      <p className="mb-1 font-[600] text-foreground">{fmtPeriod(label, bucket)}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="flex items-center gap-2 text-foreground/80">
          <span className="h-2 w-2 rounded-sm" style={{ background: p.color }} />
          {p.name}: <span className="font-[560] text-foreground">{p.dataKey.includes("bytes") ? fmtBytes(p.value) : p.value}</span>
        </p>
      ))}
    </div>
  );
}

export default function StorageTrend() {
  const [preset, setPreset] = useState("30d");
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(isoDate(new Date()));
  const [bucket, setBucket] = useState("day");
  const [compare, setCompare] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-storage-trend", from, to, bucket, compare],
    queryFn: async () => {
      const qs = new URLSearchParams({ from, to, bucket, compare: String(compare) });
      return (await api.get(`/admin/storage/trend?${qs.toString()}`)).data;
    },
  });

  const applyPreset = (p) => {
    const v = p.build();
    setPreset(p.label);
    setFrom(v.from); setTo(v.to); setBucket(v.bucket);
  };
  const onFrom = (e) => { setPreset(null); setFrom(e.target.value); };
  const onTo = (e) => { setPreset(null); setTo(e.target.value); };

  const series = data?.series || [];
  const cmp = data?.compare?.series || [];
  const merged = series.map((s, i) => ({
    period: s.period,
    cumulative_bytes: s.cumulative_bytes,
    added_bytes: s.added_bytes,
    cmp_cumulative_bytes: cmp[i] ? cmp[i].cumulative_bytes : null,
    cmp_added_bytes: cmp[i] ? cmp[i].added_bytes : null,
  }));

  const exportCsv = () => {
    const head = ["Period", "Added bytes", "Added", "Cumulative bytes", "Cumulative"];
    if (compare) head.push("Compare period", "Compare added bytes", "Compare cumulative bytes");
    const rows = [head];
    series.forEach((s, i) => {
      const r = [s.period, s.added_bytes, fmtBytes(s.added_bytes), s.cumulative_bytes, fmtBytes(s.cumulative_bytes)];
      if (compare) r.push(cmp[i]?.period || "", cmp[i]?.added_bytes ?? "", cmp[i]?.cumulative_bytes ?? "");
      rows.push(r);
    });
    const csv = rows.map((r) => r.map((c) => {
      const v = c == null ? "" : String(c);
      return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    }).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `storage-trend-${bucket}-${isoDate(new Date())}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const addedInRange = data?.added_bytes || 0;

  return (
    <Card className="mt-6 border-0 p-6" data-testid="storage-trend">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-[640] text-foreground"><TrendingUp className="h-4.5 w-4.5 text-primary" /> Storage over time</h2>
          <p className="text-sm text-muted-foreground">{fmtBytes(addedInRange)} added in this range{compare && data?.compare ? ` · vs ${fmtBytes(data.compare.added_bytes)} previous period` : ""}.</p>
        </div>
        <Button variant="secondary" size="sm" onClick={exportCsv} data-testid="storage-trend-export">
          <Download className="mr-1.5 h-4 w-4" /> Export CSV
        </Button>
      </div>

      {/* Controls */}
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div className="flex flex-wrap gap-1" data-testid="storage-trend-presets">
          {PRESETS.map((p) => (
            <Button key={p.label} size="sm" variant={preset === p.label ? "default" : "secondary"} onClick={() => applyPreset(p)} data-testid={`storage-trend-preset-${p.label}`}>{p.label}</Button>
          ))}
        </div>
        <div>
          <label className="block text-[11px] text-muted-foreground">From</label>
          <Input type="date" value={(from || "").slice(0, 10)} max={(to || "").slice(0, 10)} onChange={onFrom} className="h-9 w-[150px]" data-testid="storage-trend-from" />
        </div>
        <div>
          <label className="block text-[11px] text-muted-foreground">To</label>
          <Input type="date" value={(to || "").slice(0, 10)} min={(from || "").slice(0, 10)} max={isoDate(new Date())} onChange={onTo} className="h-9 w-[150px]" data-testid="storage-trend-to" />
        </div>
        <div>
          <label className="block text-[11px] text-muted-foreground">Bucket</label>
          <div className="flex gap-1">
            {["hour", "day", "week", "month"].map((g) => (
              <Button key={g} size="sm" variant={bucket === g ? "default" : "secondary"} onClick={() => { setPreset(null); setBucket(g); }} data-testid={`storage-trend-bucket-${g}`}>{g}</Button>
            ))}
          </div>
        </div>
        <Button size="sm" variant={compare ? "default" : "secondary"} onClick={() => setCompare((c) => !c)} data-testid="storage-trend-compare">
          <GitCompareArrows className="mr-1.5 h-4 w-4" /> Compare previous
        </Button>
      </div>

      {/* Charts */}
      <div className="mt-5 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div>
          <p className="mb-2 text-xs font-[560] text-muted-foreground">Cumulative storage</p>
          <div className="h-[260px] w-full" data-testid="storage-trend-cumulative-chart">
            {isLoading ? <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Loading…</div> : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={merged} margin={{ top: 5, right: 12, left: -8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="soStorageFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#148b8f" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#148b8f" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8eef0" vertical={false} />
                  <XAxis dataKey="period" tickFormatter={(p) => fmtPeriod(p, bucket)} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={20} />
                  <YAxis tickFormatter={fmtBytes} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={56} />
                  <Tooltip content={<TrendTooltip bucket={bucket} />} />
                  {compare && <Legend wrapperStyle={{ fontSize: 11 }} />}
                  <Area type="monotone" dataKey="cumulative_bytes" name="This period" stroke="#148b8f" strokeWidth={2} fill="url(#soStorageFill)" />
                  {compare && <Line type="monotone" dataKey="cmp_cumulative_bytes" name="Previous period" stroke="#94a3b8" strokeWidth={2} strokeDasharray="4 3" dot={false} />}
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        <div>
          <p className="mb-2 text-xs font-[560] text-muted-foreground">Storage added per {bucket}</p>
          <div className="h-[260px] w-full" data-testid="storage-trend-added-chart">
            {isLoading ? <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Loading…</div> : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={merged} margin={{ top: 5, right: 12, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8eef0" vertical={false} />
                  <XAxis dataKey="period" tickFormatter={(p) => fmtPeriod(p, bucket)} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={20} />
                  <YAxis tickFormatter={fmtBytes} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={56} />
                  <Tooltip content={<TrendTooltip bucket={bucket} />} />
                  {compare && <Legend wrapperStyle={{ fontSize: 11 }} />}
                  <Bar dataKey="added_bytes" name="This period" fill="#148b8f" radius={[3, 3, 0, 0]} maxBarSize={28} />
                  {compare && <Bar dataKey="cmp_added_bytes" name="Previous period" fill="#cbd5e1" radius={[3, 3, 0, 0]} maxBarSize={28} />}
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
